// Centralised display metadata for the six action button types.
//
// Before this file the type name, its icon, its accent colour and the sentence
// explaining what the interaction does were spread across seven JSX blocks in
// ControlPanelLiftActions and six button components — adding a type meant
// editing all of them, and the copies had already drifted (SWITCH rendered an
// "error" divider with a "success" icon; TURNBACK and ENERGIZER_TURN_BACK were
// visually indistinguishable). One record per type, one importer per concern.
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faRightLeft,
  faStopwatch,
  faHandPointer,
  faRotateLeft,
  faBoltLightning,
  faPowerOff,
} from "@fortawesome/free-solid-svg-icons";
import { ActionButtonType } from "../../../flatbuffers/generated/archive/action-button-type";
import { BankActionInterface } from "../../../interfaces/Bank.interface";

/**
 * The button-type names as BankActionInterface declares them. Kept derived from
 * the interface rather than re-typed so a schema change is a compile error here
 * instead of a silently missing config entry.
 */
export type ActionButtonTypeName = BankActionInterface["buttonType"];

/**
 * The accent hues, one per button type.
 *
 * Values are the antd 6-step palette rather than raw CSS keywords: `green`,
 * `yellow` and `orange` as CSS names are far brighter than anything else in
 * this UI, and the plain-yellow one is nearly invisible on the light theme's
 * near-white card. These keep the requested hue while staying legible on all
 * three card surfaces (#212121 dark / #fafbfc light / #16293c blue).
 */
const ACTION_ACCENT = {
  green: "#52c41a",
  yellow: "#e8c000",
  orange: "#fa8c16",
  blue: "#1890ff",
  purple: "#9254de",
  cyan: "#13c2c2",
} as const;

/** One translatable sentence: the i18n key plus its English default. */
export interface GuideText {
  key: string;
  text: string;
}

/**
 * The long-form explanation of a button type, shown in the expanded card.
 *
 * It is the SAME copy the setup guide shows (BankActionsHelpModal), because an
 * operator configuring an action and an operator running it were reading two
 * different descriptions of the same interaction — the card's one-liner said
 * TURNBACK "automatically executes a secondary action after a delay" while the
 * guide explained the countdown, and nothing kept the two in step.
 */
export interface ActionTypeGuide {
  intro: GuideText;
  /** Optional list rendered between intro and outro. */
  bullets?: GuideText[];
  outro?: GuideText;
}

export interface ActionTypeConfig {
  /** English default for the tag/label. Passed as t()'s defaultValue. */
  label: string;
  /** i18n key for the tag label. */
  labelKey: string;
  /** Long-form explanation shown in the expanded card body. */
  guide: ActionTypeGuide;
  /** Icon shown in the card's accent slot — bound to the TYPE, never to the
   *  action's label, because labels are operator-configurable free text. */
  icon: IconDefinition;
  /**
   * The card's left-edge accent colour, bound to the TYPE so the interaction a
   * row offers is readable at a glance down the list. It is deliberately NOT
   * the action's configured `color`: that one is operator free-form (two rows
   * of different types routinely share it), so it can tint the icon and the
   * command buttons but cannot carry "this is a hold-to-press vs a toggle".
   *
   * Colour is never the only cue — the icon and the type tag carry the same
   * information for anyone who cannot separate these hues.
   */
  accent: string;
  /**
   * Whether the type can render a paired secondary command at all. "PAIR" does
   * NOT promise two buttons: a paired type still hides its secondary when no
   * toggle command is bound (see ActionControls / the button components).
   */
  controls: "SINGLE" | "PAIR";
  /**
   * How many distinct commands the type BINDS — a different question from
   * `controls`, which counts operator-pressable buttons.
   *
   * The two axes genuinely disagree. HOLDBACK binds two commands but offers
   * one control, because the second is dispatched by releasing the first.
   * ON_OFF is the mirror: two controls, one command, since its off button
   * sends the same command deasserted. Collapsing them into one flag gets one
   * of those two wrong, and both were being got wrong — the wizard's Toggle
   * columns showed six unexplained gaps on rows that have no second command to
   * configure, which reads as missing configuration rather than as a property
   * of the type.
   *
   * Required, so a new type cannot be added without answering it.
   */
  commands: "ONE" | "TWO";
  /** Short helper shown on the card when the interaction is not a plain click. */
  hintKey?: string;
  hint?: string;
}

export const ACTION_TYPE_CONFIG: Record<ActionButtonTypeName, ActionTypeConfig> = {
  TOGGLE: {
    label: "Toggle",
    labelKey: "monitoringActions.type.toggle",
    guide: {
      intro: {
        key: "monitoringActions.guide.toggle.intro",
        text: "Displays two buttons — a Primary Action button and a Toggle Action button — and each one sends a different command:",
      },
      bullets: [
        {
          key: "monitoringActions.guide.toggle.bullet1",
          text: "The Primary Action button sends the first command (e.g. ON).",
        },
        {
          key: "monitoringActions.guide.toggle.bullet2",
          text: "The Toggle Action button sends the second command (e.g. OFF).",
        },
      ],
      outro: {
        key: "monitoringActions.guide.toggle.outro",
        text: "Each button is disabled on its own valid states, so a car already in the target state cannot be sent there again. Ideal when ON and OFF must be sent independently, without interfering with each other.",
      },
    },
    icon: faRightLeft,
    accent: ACTION_ACCENT.green,
    controls: "PAIR",
    commands: "TWO",
  },
  TOGGLETIMER: {
    label: "Toggle Timer",
    labelKey: "monitoringActions.type.toggleTimer",
    guide: {
      intro: {
        key: "monitoringActions.guide.toggleTimer.intro",
        text: "Shows two separate buttons, one for the Primary Action and one for the Toggle Action. Each one triggers its own command and starts a countdown of the configured length.",
      },
      outro: {
        key: "monitoringActions.guide.toggleTimer.outro",
        text: "While the countdown runs both buttons stay disabled, which prevents the command being fired repeatedly. Ideal for ON/OFF commands that need a cooldown period.",
      },
    },
    icon: faStopwatch,
    accent: ACTION_ACCENT.blue,
    controls: "PAIR",
    commands: "TWO",
  },
  HOLDBACK: {
    label: "Holdback",
    labelKey: "monitoringActions.type.holdback",
    guide: {
      intro: {
        key: "monitoringActions.guide.holdback.intro",
        // The setup guide says the operator holds the RIGHT mouse button. The
        // component binds mouseDown/mouseUp on the button itself, so it is the
        // same (left) press that is held — described here as the code behaves.
        text: "Displays a single button. Pressing it sends the first command and starts a countdown; the paired command is sent when the button is released, or on its own once the countdown reaches zero.",
      },
      outro: {
        key: "monitoringActions.guide.holdback.outro",
        text: "Ideal for short-duration actions where the second command should follow after a few seconds of holding.",
      },
    },
    icon: faHandPointer,
    accent: ACTION_ACCENT.yellow,
    controls: "SINGLE",
    commands: "TWO",
    hint: "Press and hold",
    hintKey: "monitoringActions.hint.holdback",
  },
  TURNBACK: {
    label: "Turnback",
    labelKey: "monitoringActions.type.turnback",
    guide: {
      intro: {
        key: "monitoringActions.guide.turnback.intro",
        text: "Shows one button that sends the first command on click and then starts a countdown. When the countdown reaches zero the paired command is sent automatically.",
      },
      outro: {
        key: "monitoringActions.guide.turnback.outro",
        text: "Ideal for time-based sequences where a second command must follow the first after a delay. While one Turnback is running the others stay locked.",
      },
    },
    icon: faRotateLeft,
    accent: ACTION_ACCENT.orange,
    controls: "SINGLE",
    commands: "TWO",
  },
  ENERGIZER_TURN_BACK: {
    label: "Energizer Turn Back",
    labelKey: "monitoringActions.type.energizerTurnBack",
    // Not covered by the setup guide, which documents six types and omits this
    // one. Written from what the component does, in the same voice as the rest.
    guide: {
      intro: {
        key: "monitoringActions.guide.energizerTurnBack.intro",
        text: "Shows one button that sends the first command on click and starts a countdown, exactly like Turnback; when it reaches zero the paired command is sent automatically to return the energizer to its previous state.",
      },
      outro: {
        key: "monitoringActions.guide.energizerTurnBack.outro",
        text: "Ideal for energizer sequences that must undo themselves without the operator having to send the second command.",
      },
    },
    icon: faBoltLightning,
    accent: ACTION_ACCENT.cyan,
    controls: "SINGLE",
    commands: "TWO",
  },
  SWITCH: {
    label: "Switch",
    labelKey: "monitoringActions.type.switch",
    guide: {
      intro: {
        key: "monitoringActions.guide.switch.intro",
        text: "Displays two buttons:",
      },
      bullets: [
        {
          key: "monitoringActions.guide.switch.bullet1",
          text: "The first sends two consecutive commands: the Action, then the Toggle Action once the configured delay elapses.",
        },
        {
          key: "monitoringActions.guide.switch.bullet2",
          text: "The second is a Cancel button that clears the pending step and sends the Toggle Action immediately.",
        },
      ],
      outro: {
        key: "monitoringActions.guide.switch.outro",
        text: "Ideal when an OFF command has to go out before an ON one, so the previous state is cleared before the intended command is activated.",
      },
    },
    icon: faPowerOff,
    accent: ACTION_ACCENT.purple,
    controls: "PAIR",
    commands: "TWO",
  },

  ON_OFF: {
    label: "On / Off",
    labelKey: "monitoringActions.type.onOff",
    guide: {
      intro: {
        key: "monitoringActions.guide.onOff.intro",
        text: "Displays two buttons that both send the SAME command:",
      },
      bullets: [
        {
          key: "monitoringActions.guide.onOff.bullet1",
          text: "The On button energises the command.",
        },
        {
          key: "monitoringActions.guide.onOff.bullet2",
          text: "The Off button de-energises that same command.",
        },
      ],
      outro: {
        key: "monitoringActions.guide.onOff.outro",
        text: "Use when a command has no separate opposite. Unlike Toggle, only one command is bound, so there is no second one to leave unconfigured.",
      },
    },
    icon: faPowerOff,
    accent: ACTION_ACCENT.cyan,
    controls: "PAIR",
    commands: "ONE",
  },

  SINGLE: {
    label: "Single",
    labelKey: "monitoringActions.type.single",
    guide: {
      intro: {
        key: "monitoringActions.guide.single.intro",
        text: "Displays one button that alternates the SAME command:",
      },
      bullets: [
        {
          key: "monitoringActions.guide.single.bullet1",
          text: "The first press energises the command.",
        },
        {
          key: "monitoringActions.guide.single.bullet2",
          text: "The next press de-energises it, and so on.",
        },
      ],
      outro: {
        key: "monitoringActions.guide.single.outro",
        text: "Use where one control should hold both states. Only one command is bound.",
      },
    },
    icon: faPowerOff,
    accent: ACTION_ACCENT.blue,
    controls: "SINGLE",
    commands: "ONE",
  },

  ONE_SHOT: {
    label: "One-Shot",
    labelKey: "monitoringActions.type.oneShot",
    guide: {
      intro: {
        key: "monitoringActions.guide.oneShot.intro",
        text: "Displays one button that pulses the SAME command:",
      },
      bullets: [
        {
          key: "monitoringActions.guide.oneShot.bullet1",
          text: "Pressing it energises the command.",
        },
        {
          key: "monitoringActions.guide.oneShot.bullet2",
          text: "It de-energises on its own once the timer elapses — the operator does not press anything again.",
        },
      ],
      outro: {
        key: "monitoringActions.guide.oneShot.outro",
        text: "Use where the CONTROLLER owns the off, such as registering a hall or car call: the call is latched on the pulse and cleared when the car arrives. Unlike Turnback or Switch, no second command is bound — the same one is cleared.",
      },
    },
    icon: faPowerOff,
    accent: ACTION_ACCENT.cyan,
    controls: "SINGLE",
    commands: "ONE",
  },
};

/**
 * Normalise whatever the API put in `buttonType` to a config key.
 *
 * BankActionInterface types the field as a string union, but the value reaching
 * the UI has historically been any of: the ActionButtonType enum number, that
 * number as a string, or the enum name. This mirrors the tolerant resolution
 * that used to live inline in ControlPanelLiftActions.
 *
 * Returns undefined for anything unrecognised so the caller can skip the row
 * rather than render a card with no controls.
 *
 * The empty string is rejected explicitly: `Number("")` is 0 and `Number.isNaN(0)`
 * is false, so an unset field would otherwise resolve to ActionButtonType[0] —
 * TOGGLE — and render two live command buttons for an unconfigured action.
 */
export const resolveActionButtonType = (raw: unknown): ActionButtonTypeName | undefined => {
  const fromEnumValue = (value: number): ActionButtonTypeName | undefined => {
    const name = ActionButtonType[value];
    return typeof name === "string" && name in ACTION_TYPE_CONFIG
      ? (name as ActionButtonTypeName)
      : undefined;
  };

  if (typeof raw === "number") return fromEnumValue(raw);

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed === "") return undefined;
    if (trimmed in ACTION_TYPE_CONFIG) return trimmed as ActionButtonTypeName;
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) return fromEnumValue(numeric);
  }

  return undefined;
};

/** The config for an action, or undefined when its type is unrecognised. */
export const getActionTypeConfig = (raw: unknown): ActionTypeConfig | undefined => {
  const name = resolveActionButtonType(raw);
  return name ? ACTION_TYPE_CONFIG[name] : undefined;
};

/**
 * The type a row falls back to when it carries none this UI recognises.
 *
 * SWITCH, because that is already what the setup wizard's Actions table defaults
 * an untyped row to. The two surfaces render the same `/action-banks/by-bank`
 * rows, so they cannot hold different opinions about what an untyped one is —
 * and the alternative to defaulting is not "a card without a type", it is no
 * card: the type is what selects the icon, the accent and the behaviour
 * component, so an unresolved one used to remove the row from the list entirely
 * while the wizard table went on showing it.
 *
 * SWITCH is also the safest of the six to guess: one press sends one command,
 * with no timer left running behind it.
 */
export const DEFAULT_ACTION_BUTTON_TYPE: ActionButtonTypeName = "SWITCH";

/**
 * An action's type and config, never undefined.
 *
 * This is the accessor a LIST should use: it answers for every row, so no row is
 * dropped for its type alone. `resolveActionButtonType`/`getActionTypeConfig`
 * stay honest about not recognising a value, for callers that need to know.
 */
export const actionTypeOrDefault = (
  raw: unknown,
): { typeName: ActionButtonTypeName; config: ActionTypeConfig } => {
  const typeName = resolveActionButtonType(raw) ?? DEFAULT_ACTION_BUTTON_TYPE;
  return { typeName, config: ACTION_TYPE_CONFIG[typeName] };
};

/**
 * Does this type bind a SECOND command the operator configures?
 *
 * False for ON_OFF: it renders two buttons, but both send the one bound
 * command — one asserted, one deasserted. So its paired-COMMAND settings are
 * not merely unset, they do not exist.
 */
export function bindsPairedCommand(type: ActionButtonTypeName): boolean {
  return ACTION_TYPE_CONFIG[type].commands === "TWO";
}

/**
 * Does this type render a second button, with its own label and colour?
 *
 * True for ON_OFF (two controls, one command) and for HOLDBACK/TURNBACK/
 * ENERGIZER_TURN_BACK (one control, two commands — the second button is
 * present but inert, carrying the pending command's label and countdown). Only
 * the genuinely one-button types answer false.
 */
export function rendersSecondButton(type: ActionButtonTypeName): boolean {
  const config = ACTION_TYPE_CONFIG[type];
  return config.commands === "TWO" || config.controls === "PAIR";
}
